import { useState, useEffect, useRef } from "react";
import { Phone, ShieldCheck } from "lucide-react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { getFirebaseAuth } from "../lib/firebase";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { toast } from "sonner";

const friendlyError = (err) => {
  const code = err?.code || "";
  if (code.includes("invalid-phone-number")) return "Please enter a valid 10-digit mobile number.";
  if (code.includes("too-many-requests")) return "Too many attempts. Please wait a few minutes and try again.";
  if (code.includes("invalid-verification-code")) return "Incorrect OTP. Please check the code and try again.";
  if (code.includes("code-expired")) return "This OTP has expired. Please request a new one.";
  if (code.includes("unauthorized-domain")) return "This domain is not authorized for sign-in. Please contact support.";
  if (code.includes("captcha-check-failed") || code.includes("invalid-app-credential"))
    return "Verification could not be completed. Please refresh the page and try again.";
  if (code.includes("network-request-failed")) return "Network error. Check your connection and try again.";
  if (code.includes("quota-exceeded")) return "SMS quota exceeded. Please try again later.";
  if (code.includes("web-storage-unsupported")) return "Please enable cookies/site data in your browser and try again.";
  return null;
};

const showError = (err) => {
  const friendly = friendlyError(err);
  const raw = err?.code || err?.message || "unknown";
  console.error("Firebase auth raw error:", err?.code, "|", err?.message, "|", err);
  toast.error(friendly ? `${friendly} (${raw})` : `Firebase error: ${err?.message || raw}`, { duration: 8000 });
};

export default function PhoneAuth({ onSuccess, testidPrefix = "phone-auth" }) {
  const { refresh } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // epoch is seeded UNIQUE PER MOUNT — grecaptcha caches widgets by element id,
  // so a remounted component must never reuse an id a previous instance rendered into.
  const [epoch, setEpoch] = useState(() => Math.floor(Math.random() * 1e9));
  const confirmationRef = useRef(null);
  const verifierRef = useRef(null);
  const recaptchaId = `recaptcha-${testidPrefix}-${epoch}`;

  // Create ONE RecaptchaVerifier tied to a stable container and reuse it across
  // resend/retry. It is cleared only when the component unmounts.
  useEffect(() => {
    let cancelled = false;
    const auth = getFirebaseAuth();
    const verifier = new RecaptchaVerifier(auth, recaptchaId, {
      size: "invisible",
      callback: () => {
        // captcha solved — signInWithPhoneNumber promise resolves
      },
      "expired-callback": () => {
        toast.info("Verification expired — please tap Send OTP again.");
      },
    });
    verifierRef.current = verifier;
    setCaptchaReady(false);
    verifier.render()
      .then(() => { if (!cancelled) setCaptchaReady(true); })
      .catch((e) => {
        console.error("reCAPTCHA render failed:", e);
        // last resort: remount the container with a fresh id and rebuild once
        if (!cancelled) setEpoch((x) => x + 1);
      });
    return () => {
      cancelled = true;
      try { verifier.clear(); } catch { /* already cleared or never rendered */ }
      if (verifierRef.current === verifier) verifierRef.current = null;
    };
  }, [recaptchaId]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const backToPhone = () => {
    setStep("phone");
    setOtp("");
    confirmationRef.current = null;
    // same verifier is reused — no teardown/rebuild on number change
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    const verifier = verifierRef.current;
    if (phone.length !== 10 || !verifier || !captchaReady || loading) return;
    setLoading(true);
    try {
      const confirmation = await signInWithPhoneNumber(getFirebaseAuth(), `+91${phone}`, verifier);
      confirmationRef.current = confirmation;
      setStep("otp");
      setCooldown(30);
      toast.success(`OTP sent to +91 ******${phone.slice(-4)}`);
    } catch (err) {
      showError(err);
      // verifier stays alive — the next submit re-executes reCAPTCHA on it
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!confirmationRef.current || otp.length !== 6) return;
    setLoading(true);
    try {
      const result = await confirmationRef.current.confirm(otp);
      const idToken = await result.user.getIdToken();
      const { data } = await api.post("/auth/phone/session", { id_token: idToken });
      await refresh();
      toast.success(data.account_created ? "Welcome to Humaira Crafts — your account is ready" : "Welcome back");
      onSuccess?.(data.user);
    } catch (err) {
      // axios errors carry .response (backend message); Firebase SDK errors carry .code
      if (err?.response) toast.error(formatApiError(err));
      else if (err?.code) showError(err);
      else toast.error(formatApiError(err));
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`${testidPrefix}`}>
      {step === "phone" ? (
        <div className="flex flex-col gap-4">
          <div>
            <p className="small-caps text-gold mb-2">Continue with Mobile Number</p>
            <p className="text-xs text-muted-foreground leading-relaxed">We&apos;ll send a one-time code by SMS to verify it&apos;s you. No passwords needed.</p>
          </div>
          <div className="flex items-center border border-black/15 bg-white focus-within:border-gold transition-colors duration-300">
            <span className="pl-4 pr-2 text-sm text-muted-foreground border-r border-black/10 flex items-center gap-2">
              <Phone strokeWidth={1.5} size={15} /> +91
            </span>
            <input
              required
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              onKeyDown={(e) => e.key === "Enter" && requestOtp()}
              className="w-full bg-transparent px-4 py-3.5 text-base md:text-sm outline-none tracking-widest"
              data-testid={`${testidPrefix}-phone`}
            />
          </div>
          <button type="button" onClick={requestOtp} disabled={loading || phone.length !== 10 || !captchaReady}
            className="bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
            data-testid={`${testidPrefix}-send-otp`}>
            {loading ? "Sending OTP..." : "Send OTP"}
          </button>
          {phone.length > 0 && phone.length < 10 && (
            <p className="text-xs text-destructive" data-testid={`${testidPrefix}-phone-hint`}>Enter your full 10-digit mobile number</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck strokeWidth={1.5} size={18} className="text-gold shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Enter the 6-digit code sent to <span className="text-foreground font-medium">+91 ******{phone.slice(-4)}</span>.{" "}
              <button type="button" onClick={backToPhone} className="text-gold underline" data-testid={`${testidPrefix}-change-number`}>Change number</button>
            </p>
          </div>
          <Input
            required
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="••••••"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && verify(e)}
            className="bg-white text-center text-2xl tracking-[0.5em] py-6 h-14"
            data-testid={`${testidPrefix}-otp`}
          />
          <button type="button" onClick={verify} disabled={loading || otp.length !== 6}
            className="bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
            data-testid={`${testidPrefix}-verify`}>
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>
          <button type="button" onClick={backToPhone} disabled={cooldown > 0 || loading}
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-gold transition-colors duration-300 disabled:opacity-40"
            data-testid={`${testidPrefix}-resend`}>
            {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
          </button>
        </div>
      )}
      {/* single stable invisible reCAPTCHA mount point — stays mounted across steps */}
      <div key={recaptchaId} id={recaptchaId} data-testid={`${testidPrefix}-recaptcha`} />
    </div>
  );
}
